import type { Tag, TagAutocompleteOption } from '@finapp/types';
import { useEffect, useState } from 'react';
import { apiUrl } from '../config/api';

interface UseTagAutocompleteOptions {
  onTagResolved: (tag: Tag) => void;
}

export interface UseTagAutocompleteReturn {
  inputValue: string;
  setInputValue: (value: string) => void;
  suggestions: TagAutocompleteOption[];
  isLoading: boolean;
  showSuggestions: boolean;
  setShowSuggestions: (show: boolean) => void;
  handleTagSelect: (option: TagAutocompleteOption) => Promise<void>;
  handleInputKeyDown: (e: React.KeyboardEvent) => void;
  handleAddCurrentInput: () => void;
}

/**
 * Encapsulates suggestion fetching, tag resolution (create or fetch), and
 * input state management shared between TagSelector and SingleTagSelector.
 *
 * The caller supplies `onTagResolved`, which is invoked with the fully-loaded
 * Tag once a selection is confirmed.  Each component then applies its own
 * multi- vs. single-selection logic on top.
 */
export function useTagAutocomplete({
  onTagResolved,
}: UseTagAutocompleteOptions): UseTagAutocompleteReturn {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<TagAutocompleteOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Debounced autocomplete fetch
  useEffect(() => {
    if (inputValue.length < 1) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsLoading(true);
      try {
        const response = await fetch(
          apiUrl(`tags/autocomplete?q=${encodeURIComponent(inputValue)}&limit=10`),
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setSuggestions(data.data || []);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Failed to fetch tag suggestions:', error);
      } finally {
        setIsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleTagSelect = async (option: TagAutocompleteOption) => {
    let resolvedTag: Tag;

    try {
      if (option.is_new) {
        const response = await fetch(apiUrl('tags'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ display_name: option.label }),
        });
        if (response.ok) {
          const result = await response.json();
          resolvedTag = result.data;
        } else {
          console.error('Failed to create tag');
          return;
        }
      } else {
        const response = await fetch(apiUrl(`tags/${option.value}`), {
          credentials: 'include',
        });
        if (response.ok) {
          const result = await response.json();
          resolvedTag = result.data;
        } else {
          console.error('Failed to fetch existing tag');
          return;
        }
      }
    } catch (error) {
      console.error('Failed to handle tag:', error);
      return;
    }

    onTagResolved(resolvedTag);
    setInputValue('');
    setShowSuggestions(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (suggestions.length > 0) {
        handleTagSelect(suggestions[0]!);
      } else {
        const newOption: TagAutocompleteOption = {
          value: inputValue.trim().toLowerCase(),
          label: inputValue.trim(),
          is_new: true,
        };
        handleTagSelect(newOption);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setInputValue('');
    }
  };

  const handleAddCurrentInput = () => {
    if (!inputValue.trim()) return;

    const newOption: TagAutocompleteOption = {
      value: inputValue.trim().toLowerCase(),
      label: inputValue.trim(),
      is_new: true,
    };
    handleTagSelect(newOption);
  };

  return {
    inputValue,
    setInputValue,
    suggestions,
    isLoading,
    showSuggestions,
    setShowSuggestions,
    handleTagSelect,
    handleInputKeyDown,
    handleAddCurrentInput,
  };
}
